import React, { useEffect, useState } from "react";
import { getVisitorSession, getVisitorId } from "@/lib/usePageview";

// Phase 25.16l — Bespoke page tracing watermark.
//
// Renders an extremely low-opacity (~0.30 amber on dark) inline
// watermark on bespoke ABM pages that includes the visitor's session
// id, visitor id prefix, page slug, and AWST timestamp.
//
// Why INLINE (not fixed-position):
//   - Fixed bottom-left / bottom-right corners get hidden by sticky
//     UI elements (chatbot widget, founder's-guide CTA bar)
//   - Inline elements survive both casual viewing AND every screenshot
//     scenario (top-fold, mid-fold, bottom-fold, full-page)
//   - Renders in the HTML source, so a developer-tools dump of the
//     page also carries the trace
//
// The page anchors itself injects the watermark; this component is
// a small standalone <span> that gets dropped wherever you want a
// trace point. The recommended pattern is to call it twice per
// bespoke page — once right after the bespoke ribbon, and once at
// the very bottom of the page content (before the public-site footer).
//
// Why opacity 0.30 on the amber-on-black palette:
//   - True invisible (opacity 0) won't appear in a screenshot at all
//   - 0.30 against #FFB000 reads as a faded amber that the eye
//     glides past at normal reading distance but resolves clearly
//     when zoomed, contrast-amplified, or captured as a PNG
//
// Forensic use case: If a screenshot of /ventia or
// /strategic-planning-institute ever surfaces publicly (LinkedIn
// screenshot, competitor deck, news leak), the visible session id
// maps directly to the visitor's IP / ASN / journey in the admin
// visitors panel — you know exactly which session leaked the page.
//
// Props:
//   - pageLabel: short label identifying the page (e.g. "VENTIA",
//     "SPI · BURDITT", "IWG · MERCEDES"). Used in the watermark text
//     so a screenshot tells you both WHICH page AND WHICH session.
//   - position: "top" or "bottom" — affects layout padding only.
export default function BespokeWatermark({ pageLabel, position = "top" }) {
  const [mark, setMark] = useState(null);

  useEffect(() => {
    const sess = getVisitorSession();
    const vid = getVisitorId();
    // Truncate IDs to keep the watermark concise but unique enough
    // (12 chars from session, 10 from visitor_id stripped of "v_" prefix)
    const sessShort = (sess || "").slice(0, 12);
    const vidShort = (vid || "").slice(2, 12);
    const stamp = new Date().toLocaleString("en-AU", {
      timeZone: "Australia/Perth",
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", hour12: false,
    });
    setMark({
      line: `DARA-OS · ${pageLabel} · S:${sessShort} · V:${vidShort} · ${stamp} AWST`,
    });
  }, [pageLabel]);

  if (!mark) return null;

  const padding = position === "top" ? "py-1.5" : "py-3";

  return (
    <div
      className={`${padding} px-4 sm:px-6 select-none font-mono text-[9px] tracking-wider whitespace-nowrap text-[#FFB000]/30 leading-none overflow-hidden bg-black`}
      aria-hidden="true"
      data-testid={`bespoke-watermark-${position}`}
    >
      {mark.line}
    </div>
  );
}
